import {
	SEASON_RULE_MALE_MIXED,
	queryFieldPositions,
} from './definitions'
import { normalizeEndRoster } from './rosterAnalysis'

export const MALE_MIXED_VIOLATION_PENALTY = 1000000000000

type RuleLineup = 'start' | 'end'
type RuleThird = 'attack' | 'mid' | 'defence' | 'A' | 'C' | 'D'

type EvaluateMaleMixedRosterRuleProps = {
	roster: RosterData
	endRoster?: RosterData
	subs?: Subs
	nulled?: RosterPositionIndex[]
	numPeriods: NumPeriods
	gameFormat: GameFormat
	seasonRules?: SeasonRules
	malePlayers: {
		[key: PlayerId]: boolean | undefined
	}
}

export type MaleMixedViolation = {
	qtr: number
	lineup: RuleLineup
	third: RuleThird
	players: PlayerId[]
}

export function evaluateMaleMixedRosterRule(props: EvaluateMaleMixedRosterRuleProps) {
	const {
		roster,
		endRoster,
		subs = [ ],
		nulled = [ ],
		numPeriods,
		gameFormat,
		seasonRules,
		malePlayers,
	} = props

	if (seasonRules !== SEASON_RULE_MALE_MIXED || !roster.length) {
		return {
			hasViolation: false,
			violations: [ ] as MaleMixedViolation[],
			penalty: 0,
		}
	}

	const fieldPositions = queryFieldPositions(gameFormat)
	const normalizedEndRoster = normalizeEndRoster(roster, endRoster, subs, numPeriods, gameFormat)
	const playersPerQtr = roster.length / numPeriods
	const nulledSet = new Set(nulled)
	const violations: MaleMixedViolation[] = [ ]

	for (let qtr = 0; qtr < numPeriods; qtr += 1) {
		const startLineup = createEmptyThirdMap()
		const endLineup = createEmptyThirdMap()
		let hasChanges = false

		for (let posIndex = 0; posIndex < fieldPositions.length; posIndex += 1) {
			const rosterIndex = qtr * playersPerQtr + posIndex

			if (nulledSet.has(rosterIndex)) {
				continue
			}

			const third = queryThird(fieldPositions[posIndex])
			if (!third) {
				continue
			}

			const startPlayer = roster[rosterIndex]
			const endPlayer = normalizedEndRoster[rosterIndex]
			hasChanges ||= endPlayer !== startPlayer

			addMalePlayer(startLineup[third], startPlayer, malePlayers)
			addMalePlayer(endLineup[third], endPlayer, malePlayers)
		}

		pushViolations(violations, qtr, 'start', startLineup)
		if (hasChanges) {
			pushViolations(violations, qtr, 'end', endLineup)
		}
	}

	return {
		hasViolation: violations.length > 0,
		violations,
		penalty: violations.length * MALE_MIXED_VIOLATION_PENALTY,
	}
}

function createEmptyThirdMap(): Record<RuleThird, Set<PlayerId>> {
	return {
		attack: new Set<PlayerId>(),
		mid: new Set<PlayerId>(),
		defence: new Set<PlayerId>(),
		A: new Set<PlayerId>(),
		C: new Set<PlayerId>(),
		D: new Set<PlayerId>(),
	}
}

function addMalePlayer(
	target: Set<PlayerId>,
	playerId: PlayerId | undefined,
	malePlayers: {
		[key: PlayerId]: boolean | undefined
	},
) {
	if (playerId && malePlayers[playerId]) {
		target.add(playerId)
	}
}

function pushViolations(
	violations: MaleMixedViolation[],
	qtr: number,
	lineup: RuleLineup,
	thirds: Record<RuleThird, Set<PlayerId>>,
) {
	Object.entries(thirds).forEach(([ third, players ]) => {
		if (players.size > 1) {
			violations.push({
				qtr,
				lineup,
				third: third as RuleThird,
				players: [ ...players ],
			})
		}
	})
}

function queryThird(position: FieldPosition): RuleThird | null {
	switch (position) {
		case 'GS':
		case 'GA':
			return 'attack'
		case 'WA':
		case 'C':
		case 'WD':
			return 'mid'
		case 'GD':
		case 'GK':
			return 'defence'
		case 'A':
		case 'D':
			return position
		default:
			return null
	}
}
